import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'react-phone-number-input';
import Input from '../../../../../components/Input';
import Dropdown from '../../../../../components/Dropdown';
import Switch from '../../../../../components/Switch';
import Button from '../../../../../components/Button';
import PhoneInput from '../../../../../components/PhoneInput';
import Toast from '../../../../../components/Toast';
import StockistBranchService from '../../../../../services/stockist.branch.service';
import CommonService from '../../../../../services/common.service';
import UserService from '../../../../../services/user.service';
import TemplateService from '../../../../../services/template.service';
import useFetch from '../../../../../hooks/useFetch';
import { GENERIC_LOOKUP_KEYS, APP_ERROR_MESSAGES, APP_SUCCESS_MESSAGES, VALIDATION_FIELD_MAPPING, DEFAULTS } from '../../../../../utils/appConstant';
import { toast } from 'react-toastify';
import { validateBranchForm } from '../validations';
import { AddEditApi } from '../../../../../interfaces/stockist.interface';

interface BranchAddEditProps {
  id?: number | null;
  stockistId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const initialFormData = {
  [VALIDATION_FIELD_MAPPING.name]: '',
  [VALIDATION_FIELD_MAPPING.address]: '',
  [VALIDATION_FIELD_MAPPING.city]: '',
  [VALIDATION_FIELD_MAPPING.state]: '',
  [VALIDATION_FIELD_MAPPING.pincode]: '',
  [VALIDATION_FIELD_MAPPING.email]: '',
  [VALIDATION_FIELD_MAPPING.primaryContactPerson]: '',
  [VALIDATION_FIELD_MAPPING.primaryContactNumber]: '',
  [VALIDATION_FIELD_MAPPING.primaryContactEmail]: '',
  [VALIDATION_FIELD_MAPPING.status]: true,
  [VALIDATION_FIELD_MAPPING.template]: '',
  [VALIDATION_FIELD_MAPPING.mrUser]: '',
  stockist_id: 0,
};

const BranchAddEdit: React.FC<BranchAddEditProps> = ({ id, stockistId, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<any>({ ...initialFormData, stockist_id: stockistId });
  const [loading, setLoading] = useState(false);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    city: '',
    state: '',
    [VALIDATION_FIELD_MAPPING.mrUser]: '',
    [VALIDATION_FIELD_MAPPING.template]: '',
  });

  const [validation, setValidation] = useState<any>({ validate: true, fields: [] });

  useFetch({
    service: StockistBranchService,
    func: 'get',
    payload: id,
    dependency: [id],
    skip: !id,
    onSuccess: (data: any) => {
      setFormData({
        [VALIDATION_FIELD_MAPPING.name]: data.name,
        [VALIDATION_FIELD_MAPPING.address]: data.address,
        [VALIDATION_FIELD_MAPPING.city]: data.city?.code,
        [VALIDATION_FIELD_MAPPING.state]: data.state?.code,
        [VALIDATION_FIELD_MAPPING.pincode]: data.pincode,
        [VALIDATION_FIELD_MAPPING.email]: data.email,
        [VALIDATION_FIELD_MAPPING.primaryContactPerson]: data.primary_contact_person,
        [VALIDATION_FIELD_MAPPING.primaryContactNumber]: data.primary_contact_number,
        [VALIDATION_FIELD_MAPPING.primaryContactEmail]: data.primary_contact_email,
        [VALIDATION_FIELD_MAPPING.status]: data.is_active,
        [VALIDATION_FIELD_MAPPING.template]: data.template_id ? String(data.template_id) : '',
        [VALIDATION_FIELD_MAPPING.mrUser]: data.mr_user_id ? String(data.mr_user_id) : '',
        stockist_id: stockistId,
      });
      if (data.city?.value) {
        setDropdownSearchData((prev: any) => ({ ...prev, city: data.city.value }));
      }
      if (data.state?.value) {
        setDropdownSearchData((prev: any) => ({ ...prev, state: data.state.value }));
      }
    },
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: { type: 'city', search: '' },
    dependency: [],
  });
  const { data: cities } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: { type: 'city', search: dropdownSearchData.city },
    dependency: [dropdownSearchData.city],
  });

  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: { type: 'state', search: '' },
    dependency: [],
  });
  const { data: states } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: { type: 'state', search: dropdownSearchData.state },
    dependency: [dropdownSearchData.state],
  });

  // MR Users lookup
  const { data: allMrUsers } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: '', role: DEFAULTS.mrRole },
    dependency: [],
  });
  const { data: mrUsers } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData[VALIDATION_FIELD_MAPPING.mrUser], role: DEFAULTS.mrRole },
    dependency: [dropdownSearchData[VALIDATION_FIELD_MAPPING.mrUser]],
  });

  // Templates lookup
  const { data: allTemplates } = useFetch({
    service: TemplateService,
    func: 'lookup',
    payload: { searchText: '' },
    dependency: [],
  });
  const { data: templates } = useFetch({
    service: TemplateService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData[VALIDATION_FIELD_MAPPING.template] },
    dependency: [dropdownSearchData[VALIDATION_FIELD_MAPPING.template]],
  });

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      if (name === VALIDATION_FIELD_MAPPING.primaryContactNumber) {
        setFormData((prev: any) => {
          return {
            ...prev,
            [name]: event.target.value,
          };
        });
      } else {
        let value: any;
        if (name === VALIDATION_FIELD_MAPPING.status) {
          value = event.target.checked;
        } else {
          value = event.target.value;
        }

        setFormData((prev: any) => {
          return {
            ...prev,
            [name]: value,
          };
        });
      }
    },
    []
  );

  const handleInputBlur = useCallback(
    (e: any, name: string) => {
      const newValidation = validateBranchForm(
        { ...formData, [name]: e.target.value.trim() },
        name
      );
      setValidation(newValidation);
    },
    [formData]
  );

  const handleDropdownSearchChange = useCallback(
    (e: any, name: string) => {
      setDropdownSearchData((prev: any) => ({ ...prev, [name]: e.target.value }));
    },
    []
  );

  const handleSubmit = async () => {
    const validations = validateBranchForm(formData);
    setValidation(validations);
    if (!validations.validate) return;

    setLoading(true);
    try {
      const payload: AddEditApi = {
        name: formData[VALIDATION_FIELD_MAPPING.name],
        address: formData[VALIDATION_FIELD_MAPPING.address],
        city: formData[VALIDATION_FIELD_MAPPING.city],
        state: formData[VALIDATION_FIELD_MAPPING.state],
        pincode: formData[VALIDATION_FIELD_MAPPING.pincode],
        email: formData[VALIDATION_FIELD_MAPPING.email],
        primary_contact_person: formData[VALIDATION_FIELD_MAPPING.primaryContactPerson],
        primary_contact_number: formData[VALIDATION_FIELD_MAPPING.primaryContactNumber],
        primary_contact_email: formData[VALIDATION_FIELD_MAPPING.primaryContactEmail],
        is_active: formData[VALIDATION_FIELD_MAPPING.status],
        stockist_id: stockistId,
        template_id: Number(formData[VALIDATION_FIELD_MAPPING.template]),
        mr_user_id: Number(formData[VALIDATION_FIELD_MAPPING.mrUser]),
        website: '', // Not used for branch
        gst_number: '', // Not used for branch
      } as any;

      if (id) {
        await StockistBranchService.edit(String(id), payload);
        toast.success(<Toast message={APP_SUCCESS_MESSAGES.stockistBranchUpdated} />, { icon: false });
      } else {
        await StockistBranchService.add(payload);
        toast.success(<Toast message={APP_SUCCESS_MESSAGES.stockistBranchCreated} />, { icon: false });
      }
      onSuccess();
    } catch (error) {
      // Error handled by http interceptor usually, or generic toast
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (field: string) => {
    if (validation.validate && validation.fields.length === 0) return undefined;
    return validation.fields.find((f: any) => f.field === field)?.message;
  };

  return (
    <div className="d-flex flex-column h-100">
      <div className="row g-6">
        <h6 className="mb-0">1. {t('stockists.addEdit.pageTexts.form.sections.stockistDetails')}</h6>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.name.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.name}
              name={VALIDATION_FIELD_MAPPING.name}
              value={formData[VALIDATION_FIELD_MAPPING.name]}
              placeholder={t('stockists.addEdit.pageTexts.form.name.placeholder')}
              onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.name)}
              onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.name)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.name)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.name)}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.email.label')}
              type="email"
              id={VALIDATION_FIELD_MAPPING.email}
              name={VALIDATION_FIELD_MAPPING.email}
              value={formData[VALIDATION_FIELD_MAPPING.email]}
              placeholder={t('stockists.addEdit.pageTexts.form.email.placeholder')}
              onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.email)}
              onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.email)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.email)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.email)}
            />
          </div>
        </div>

        <div className="col-12">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.address.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.address}
              name={VALIDATION_FIELD_MAPPING.address}
              value={formData[VALIDATION_FIELD_MAPPING.address]}
              placeholder={t('stockists.addEdit.pageTexts.form.address.placeholder')}
              onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.address)}
              onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.address)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.address)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.address)}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Dropdown
              label={t('stockists.addEdit.pageTexts.form.city.label')}
              required
              values={formData[VALIDATION_FIELD_MAPPING.city] ? [formData[VALIDATION_FIELD_MAPPING.city]] : []}
              options={(cities || []).map((city: any) => ({
                label: city.name,
                value: String(city.id),
              }))}
              allOptions={(allCities || []).map((city: any) => ({
                label: city.name,
                value: String(city.id),
              }))}
              id={VALIDATION_FIELD_MAPPING.city}
              name={VALIDATION_FIELD_MAPPING.city}
              searchText={dropdownSearchData.city}
              onSearch={handleDropdownSearchChange}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={t('stockists.addEdit.pageTexts.form.city.placeholder')}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.city)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.city)}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Dropdown
              label={t('stockists.addEdit.pageTexts.form.state.label')}
              required
              values={formData[VALIDATION_FIELD_MAPPING.state] ? [formData[VALIDATION_FIELD_MAPPING.state]] : []}
              options={(states || []).map((state: any) => ({
                label: state.name,
                value: String(state.id),
              }))}
              allOptions={(allStates || []).map((state: any) => ({
                label: state.name,
                value: String(state.id),
              }))}
              id={VALIDATION_FIELD_MAPPING.state}
              name={VALIDATION_FIELD_MAPPING.state}
              searchText={dropdownSearchData.state}
              onSearch={handleDropdownSearchChange}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={t('stockists.addEdit.pageTexts.form.state.placeholder')}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.state)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.state)}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.pincode.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.pincode}
              name={VALIDATION_FIELD_MAPPING.pincode}
              value={formData[VALIDATION_FIELD_MAPPING.pincode]}
              placeholder={t('stockists.addEdit.pageTexts.form.pincode.placeholder')}
              onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.pincode)}
              onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.pincode)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.pincode)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.pincode)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">2. {t('stockists.addEdit.pageTexts.form.sections.primaryContactDetails')}</h6>

        <div className="col-12">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.primaryContactPerson.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.primaryContactPerson}
              name={VALIDATION_FIELD_MAPPING.primaryContactPerson}
              value={formData[VALIDATION_FIELD_MAPPING.primaryContactPerson]}
              placeholder={t('stockists.addEdit.pageTexts.form.primaryContactPerson.placeholder')}
              onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.primaryContactPerson)}
              onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.primaryContactPerson)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactPerson)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactPerson)}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              required
              defaultCountry="CA"
              id={VALIDATION_FIELD_MAPPING.primaryContactNumber}
              name={VALIDATION_FIELD_MAPPING.primaryContactNumber}
              label={t('stockists.addEdit.pageTexts.form.primaryContactNumber.label')}
              placeholder={t('stockists.addEdit.pageTexts.form.primaryContactNumber.placeholder')}
              value={formData[VALIDATION_FIELD_MAPPING.primaryContactNumber]}
              onChange={handleInputChange}
              onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.primaryContactNumber)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactNumber)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactNumber)}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.primaryContactEmail.label')}
              type="email"
              id={VALIDATION_FIELD_MAPPING.primaryContactEmail}
              name={VALIDATION_FIELD_MAPPING.primaryContactEmail}
              value={formData[VALIDATION_FIELD_MAPPING.primaryContactEmail]}
              placeholder={t('stockists.addEdit.pageTexts.form.primaryContactEmail.placeholder')}
              onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.primaryContactEmail)}
              onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.primaryContactEmail)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactEmail)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactEmail)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">3. {t('stockists.addEdit.pageTexts.form.sections.otherInformation')}</h6>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Dropdown
              label={t('stockists.stockistBranch.addEdit.pageTexts.form.template.label')}
              required
              values={formData[VALIDATION_FIELD_MAPPING.template] ? [formData[VALIDATION_FIELD_MAPPING.template]] : []}
              options={(templates || []).map((tpl: any) => ({
                label: tpl.name,
                value: String(tpl.id),
              }))}
              allOptions={(allTemplates || []).map((tpl: any) => ({
                label: tpl.name,
                value: String(tpl.id),
              }))}
              id={VALIDATION_FIELD_MAPPING.template}
              name={VALIDATION_FIELD_MAPPING.template}
              searchText={dropdownSearchData[VALIDATION_FIELD_MAPPING.template]}
              onSearch={handleDropdownSearchChange}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={t('stockists.stockistBranch.addEdit.pageTexts.form.template.placeholder')}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.template)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.template)}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Dropdown
              label={t('stockists.stockistBranch.addEdit.pageTexts.form.mrUser.label')}
              required
              values={formData[VALIDATION_FIELD_MAPPING.mrUser] ? [formData[VALIDATION_FIELD_MAPPING.mrUser]] : []}
              options={(mrUsers || []).map((user: any) => ({
                label: `${user.name}`,
                value: String(user.id),
              }))}
              allOptions={(allMrUsers || []).map((user: any) => ({
                label: `${user.name}`,
                value: String(user.id),
              }))}
              id={VALIDATION_FIELD_MAPPING.mrUser}
              name={VALIDATION_FIELD_MAPPING.mrUser}
              searchText={dropdownSearchData[VALIDATION_FIELD_MAPPING.mrUser]}
              onSearch={handleDropdownSearchChange}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={t('stockists.stockistBranch.addEdit.pageTexts.form.mrUser.placeholder')}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.mrUser)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.mrUser)}
            />
          </div>
        </div>

        <div className="col-md-3 d-flex align-items-center">
          <div className="form-floating form-floating-outline">
            <Switch
              label={
                formData[VALIDATION_FIELD_MAPPING.status]
                  ? t('stockists.addEdit.pageTexts.form.status.activeLabel')
                  : t('stockists.addEdit.pageTexts.form.status.inActiveLabel')
              }
              checked={formData[VALIDATION_FIELD_MAPPING.status]}
              name={VALIDATION_FIELD_MAPPING.status}
              onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.status)}
            />
          </div>
        </div>

      </div>
      <div className="mt-auto pt-4 d-flex justify-content-end gap-3">
        <Button
          className="btn btn-outline-secondary"
          text={t('stockists.addEdit.pageTexts.form.buttons.cancel')}
          onClick={onClose}
          disabled={loading}
        />
        <Button
          className="btn btn-primary"
          text={t('stockists.addEdit.pageTexts.form.buttons.save')}
          onClick={handleSubmit}
          loading={loading}
          disabled={loading}
        />
      </div>
    </div>
  );
};

export default BranchAddEdit;
